import Config from '@config';
import { createApi } from '@reduxjs/toolkit/query/react';

import {
  Appeal,
  AppealDetail,
  CreateAppealParams,
  CreateAppealResult,
  GetAppealsParams,
  SendAppealMessageParams,
  SendAppealMessageResult,
} from '@/features/appeals/types';
import axiosBaseQuery from '@/utils/axiosBaseQuery';

export const appealsApi = createApi({
  reducerPath: 'appealsApi',
  baseQuery: axiosBaseQuery({
    baseUrl: Config.VITE_API_URL + '/appeals',
  }),
  tagTypes: ['Appeal', 'AppealDetail'],
  endpoints: builder => ({
    getAppeals: builder.query<Appeal[], GetAppealsParams>({
      query: params => ({
        url: '',
        params,
      }),
      providesTags: ['Appeal'],
    }),
    getAppealById: builder.query<AppealDetail, string>({
      query: id => ({ url: `/${id}` }),
      providesTags: (_result, _error, id) => [{ type: 'AppealDetail', id }],
    }),
    createAppeal: builder.mutation<CreateAppealResult, CreateAppealParams>({
      query: ({ files, ...fields }) => {
        const formData = new FormData();
        Object.entries(fields).forEach(([key, value]) => {
          if (value === undefined || value === null) return;
          if (Array.isArray(value)) {
            value.forEach(item => formData.append(key, item));
            return;
          }
          formData.append(key, value);
        });
        files?.forEach(file => formData.append('files', file));

        return {
          url: '',
          method: 'POST',
          data: formData,
        };
      },
      invalidatesTags: ['Appeal'],
    }),
    sendAppealMessage: builder.mutation<SendAppealMessageResult, SendAppealMessageParams>({
      query: ({ appealId, body, files }) => {
        const formData = new FormData();
        formData.append('body', body);
        files?.forEach(file => formData.append('files', file));

        return {
          url: `/${appealId}/messages`,
          method: 'POST',
          data: formData,
        };
      },
      invalidatesTags: (_result, _error, { appealId }) => [
        'Appeal',
        { type: 'AppealDetail', id: appealId },
      ],
    }),
    closeAppeal: builder.mutation<void, string>({
      query: id => ({ url: `/${id}/close`, method: 'POST' }),
      invalidatesTags: (_result, _error, id) => ['Appeal', { type: 'AppealDetail', id }],
    }),
    reopenAppeal: builder.mutation<void, string>({
      query: id => ({ url: `/${id}/reopen`, method: 'POST' }),
      invalidatesTags: (_result, _error, id) => ['Appeal', { type: 'AppealDetail', id }],
    }),
    markAppealRead: builder.mutation<void, string>({
      query: id => ({ url: `/${id}/read`, method: 'POST' }),
      invalidatesTags: (_result, _error, id) => ['Appeal', { type: 'AppealDetail', id }],
    }),
  }),
});

export const {
  useGetAppealsQuery,
  useGetAppealByIdQuery,
  useCreateAppealMutation,
  useSendAppealMessageMutation,
  useCloseAppealMutation,
  useReopenAppealMutation,
  useMarkAppealReadMutation,
} = appealsApi;
